import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import styled from 'styled-components';

import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { CoverImagesProjectResource } from '@/types/resources';

const Box = styled.div`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  background: ${({ theme }) => theme.colors.white};
  margin-bottom: 48px;
`;

const BoxHeader = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  padding: 28px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BoxInner = styled.div`
  position: relative;
  display: grid;
  grid-gap: 24px 48px;
  grid-template-columns: 1fr 1fr;
  padding: 24px;
`;

const Divider = styled.div`
  position: absolute;
  left: 50%;
  top: 24px;
  bottom: 24px;
  width: 1px;
  background-color: ${({ theme }) => theme.colors.lightGray};
`;

const Title = styled(Text)`
  line-height: 22px;
  margin-top: 6px;
`;

const BpLink = styled(Title)`
  display: block;
  color: ${({ theme }) => theme.colors.purple};
`;

interface Props {
  article: CoverImagesProjectResource;
}

const ArticleBox: FC<Props> = ({ article }) => {
  const [showContent, setShowContent] = useState(true);

  return (
    <Box>
      <BoxHeader>
        <Text $type="button">Article Details</Text>
        <ShowHideButton
          showContent={showContent}
          onClick={() => setShowContent((prev) => !prev)}
        />
      </BoxHeader>
      {showContent && (
        <BoxInner>
          <Divider />
          <div>
            <Text $type="smallBold" $color="gray">
              Article Name
            </Text>
            <Title $type="caption">{article.articleTitle}</Title>
          </div>
          <div>
            <Text $type="smallBold" $color="gray">
              Date Created
            </Text>
            <Title $type="caption">
              {article.createdInBpAt
                ? DateTime.fromSQL(article.createdInBpAt).toFormat(
                    'dd/LL/yyyy t',
                  )
                : '-'}
            </Title>
          </div>
          <div>
            <Text $type="smallBold" $color="gray">
              Editor
            </Text>
            <Title $type="caption">{article.editor ?? '-'}</Title>
          </div>
          <div>
            <Text $type="smallBold" $color="gray">
              Variations
            </Text>
            <Title $type="caption">{article.images?.length ?? 0}</Title>
          </div>
          <div>
            <Text $type="smallBold" $color="gray">
              Date Added
            </Text>
            <Title $type="caption">
              {article.createdAt
                ? DateTime.fromISO(article.createdAt).toFormat('dd/LL/yyyy t')
                : '-'}
            </Title>
          </div>
          <div>
            <Text $type="smallBold" $color="gray">
              Specialist
            </Text>
            <Title $type="caption">{article.createdBy}</Title>
          </div>
          <div>
            <Text $type="smallBold" $color="gray">
              Article Link
            </Text>
            <BpLink
              href={`https://www.boredpanda.com/?p=${article.articleId}`}
              as="a"
              $type="caption"
              target="_blank"
              rel="nofollow noreferrer"
            >
              https://www.boredpanda.com/?p=${article.articleId}
            </BpLink>
          </div>
        </BoxInner>
      )}
    </Box>
  );
};

export default ArticleBox;
